/**
 * AdminBackhaulRateWidget
 *
 * Per-country backhaul conversion: % of logistics loads (last N days)
 * with at least one matching carrier. Sales asset for partner pitch.
 *
 * Backend: GET /api/admin/p1/backhaul-stats (admin-only, 5-min cache).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/lib/context';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('keikka_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const rateColor = (pct) => {
  if (pct >= 80) return 'text-emerald-700';
  if (pct >= 50) return 'text-amber-700';
  return 'text-rose-700';
};

export const AdminBackhaulRateWidget = ({ days = 7 }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/p1/backhaul-stats`, {
        headers: authHeaders(),
        params: { include_demo: includeDemo, days },
      });
      setData(res.data);
    } catch (_) {
      setData({ rows: [], totals: { loads: 0, matched: 0, carriers: 0 }, error: true });
    } finally {
      setLoading(false);
    }
  }, [includeDemo, days]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const totals = data?.totals || { loads: 0, matched: 0, carriers: 0 };

  return (
    <Card data-testid="admin-backhaul-widget" className="border-slate-200">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="h-4 w-4 text-slate-500" />
            {t('adminP1.backhaul.title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('adminP1.backhaul.subtitle', { days })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <span>{t('adminP1.budget.includeDemo')}</span>
            <Switch
              checked={includeDemo}
              onCheckedChange={setIncludeDemo}
              data-testid="admin-backhaul-include-demo"
            />
          </label>
          <Button
            type="button" variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="admin-backhaul-refresh"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="ml-1.5">{t('adminP1.budget.refresh')}</span>
          </Button>
          {data && (
            <Badge
              variant="outline"
              className={data.cached ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}
              data-testid="admin-backhaul-cache-state"
            >
              {data.cached ? t('adminP1.budget.cached') : t('adminP1.budget.live')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-xs text-slate-500 mb-2" data-testid="admin-backhaul-totals">
          {t('adminP1.backhaul.totals', { loads: totals.loads, matched: totals.matched, carriers: totals.carriers })}
        </div>

        {rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-muted-foreground"
               data-testid="admin-backhaul-empty">
            {t('adminP1.backhaul.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 font-medium">{t('adminP1.backhaul.col.country')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.backhaul.col.loads')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.backhaul.col.matched')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.backhaul.col.rate')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.backhaul.col.carriers')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.country}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`admin-backhaul-row-${r.country}`}>
                    <td className="py-2 font-medium">
                      {t(`countries.${r.country}`, { defaultValue: r.country })}
                    </td>
                    <td className="py-2 text-right tabular-nums">{r.loads}</td>
                    <td className="py-2 text-right tabular-nums">{r.matched}</td>
                    <td className={`py-2 text-right font-semibold tabular-nums ${rateColor(r.rate_pct)}`}>
                      {r.rate_pct.toFixed(1)}%
                    </td>
                    <td className="py-2 text-right tabular-nums text-slate-600">{r.carriers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBackhaulRateWidget;
